import TableOnly from "components/DataTable/TableOnly";
import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

function LeadContact({ selectedLead }) {
  const user = useSelector((state) => state.user?.userInfo);
  const dispatch = useDispatch();

  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [leadContactData, setLeadContactData] = useState([]);
  const [defaultContact, setDefaultContact] = useState(null);

  const [loading, setLoading] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(5);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /*   All Function
   ********************************************* */
  // Get Lead Contacts
  const getLeadContacts = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      if (!selectedLead?.leadId) return;
      try {
        setLoading(true);

        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/lead-contacts/list/" +
            selectedLead?.leadId +
            "?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() }
        );
        if (data?.success) {
          let contacts = data?.data || [];
          let _defaultContact = data?.defaultContact;
          setDefaultContact(_defaultContact || null);
          setLeadContactData(contacts || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, pageNumber, pageLimit, filterBy.value, selectedLead]
  );

  // Adds Button Function
  const handleAddLeadContact = async (e) => {
    e.preventDefault();
    if (!selectedLead?.leadId) return;
    try {
      setLoading(true);
      const { data } = await API.post(
        "/lead-contact/add/" + selectedLead?.leadId,
        formData,
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        setFormData({});
        setShowAddModal(false);
        setPageNumber(1);
        getLeadContacts({ page: 1 });
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }

    setShowAddModal(false);
  };

  // Update Button Function
  const handleUpdateLeadContact = async (e) => {
    e.preventDefault();
    if (!selectedLead?.leadId) return;
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/lead-contact/" + selectedLead?.leadId,
        formData,
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        setIsUpdateMode(false);
        setShowAddModal(false);
        getLeadContacts();
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Sorting Up
  const sortUp = async (id) => {
    if (!selectedLead?.leadId) return;
    setLoading(true);
    try {
      const { data } = await API.post(
        "/lead-contact/sort-up",
        { contactId: id, leadId: selectedLead?.leadId },
        { headers: authHeader() }
      );
      if (data.success) {
        await getLeadContacts();
        setLoading(false);
      } else {
        setLoading(false);
        dispatch(
          setNotification({
            message: data?.message || "Failed to sort up",
            type: "error",
          })
        );
      }
    } catch (err) {
      setLoading(false);
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  };

  // Sorting Down
  const sortDown = async (id) => {
    if (!selectedLead?.leadId) return;
    setLoading(true);
    try {
      const { data } = await API.post(
        "/lead-contact/sort-down",
        { contactId: id, leadId: selectedLead?.leadId },
        { headers: authHeader() }
      );
      if (data.success) {
        await getLeadContacts();
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  };

  // Delete Lead Contact
  const deleteLeadContact = async (id) => {
    try {
      const { data } = await API.delete("/lead-contact/" + id, {
        headers: authHeader(),
      });
      if (data.success) {
        await getLeadContacts();
        dispatch(
          setNotification({
            message: "Deleted Successfully",
            type: "success",
          })
        );
        return;
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  // set Default Contact
  const handleDefaultSelect = async (item) => {
    if (!selectedLead?.leadId) return;
    setLoading(true);
    try {
      const { data } = await API.post(
        "/lead-contact/set-default",
        {
          contactId: item.contactId,
          leadId: selectedLead?.leadId,
        },
        { headers: authHeader() }
      );

      if (data?.success) {
        await getLeadContacts();
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to set default",
            type: "error",
          })
        );
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  const copyContent = (val) => {
    navigator.clipboard.writeText(val);
    dispatch(
      setNotification({
        message: "Email Copied",
        type: "success",
      })
    );
  };

  /*   Table Functions
   ********************************************* */

  const btnLeft = () => (
    <div className="d-flex">
      <h4 className="border-secondary border-bottom">LEAD CONTACTS</h4>
    </div>
  );

  let filters = [
    { name: "First Name", value: "firstName" },
    { name: "Last Name", value: "lastName" },
    { name: "Department", value: "department" },
    { name: "Phone", value: "phone" },
    { name: "Mobile Phone", value: "mobile" },
    { name: "Email", value: "email" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}{" "}
        <i className="mdi mdi-chevron-down"></i>
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "First Name", value: "firstName" },
    { name: "Last Name", value: "lastName" },
    { name: "Department", value: "department" },
    { name: "Phone", value: "phone" },
    { name: "Mobile Phone", value: "mobile" },
    { name: "Email", value: "email" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr
      className={`${item?.isDefault ? "bg-primary bg-opacity-10" : ""}`}
      key={item?.id}
    >
      <td className="py-0 pe-0 align-middle">{item?.firstName}</td>
      <td className="py-0 pe-0 align-middle">{item?.lastName}</td>
      <td className="py-0 pe-0 align-middle">{item?.department} </td>
      <td className="py-0 pe-0 align-middle">{item?.phone} </td>
      <td className="py-0 pe-0 align-middle">{item?.mobile} </td>
      <td
        onDoubleClick={() => {
          copyContent(item?.email);
        }}
        className="py-2 pe-0 align-middle user-select-none"
      >
        {item?.email}{" "}
      </td>
      <td
        className="table-action py-0 text-end align-middle
      "
      >
        {(user?.permissions?.includes("lead_update") ||
          user?.roles === "admin") && (
          <button
            onClick={() => {
              sortUp(item.contactId);
            }}
            disabled={index === 0}
            className="btn btn-sm px-1"
          >
            <i className="mdi mdi-chevron-double-up p-0"></i>
          </button>
        )}
        {(user?.permissions?.includes("lead_update") ||
          user?.roles === "admin") && (
          <button
            onClick={() => {
              sortDown(item?.contactId);
            }}
            disabled={index === leadContactData.length - 1}
            className="btn btn-sm px-1"
          >
            <i className="mdi mdi-chevron-double-down"></i>
          </button>
        )}
        {(user?.permissions?.includes("lead_update") ||
          user?.roles === "admin") && (
          <button
            onClick={() => {
              setFormData(item);
              setIsUpdateMode(true);
              setShowAddModal(true);
            }}
            className="btn btn-sm px-1"
          >
            <i className="mdi mdi-pencil"></i>
          </button>
        )}
        {(user?.permissions?.includes("lead_delete") ||
          user?.roles === "admin") && (
          <DeleteModalButton
            deleteFunc={deleteLeadContact}
            deleteParams={item?.contactId}
            btnClass={"btn btn-sm px-1"}
            disableConditions={loading}
          />
        )}
      </td>
    </tr>
  );

  const topRow = () => (
    <tr
      className={`${
        defaultContact?.isDefault ? "bg-primary bg-opacity-10" : ""
      }`}
      key={defaultContact?.contactId || defaultContact?.id}
    >
      <td className="py-0 pe-0 align-middle">{defaultContact?.firstName}</td>
      <td className="py-0 pe-0 align-middle">{defaultContact?.lastName}</td>
      <td className="py-0 pe-0 align-middle">{defaultContact?.department} </td>
      <td className="py-0 pe-0 align-middle">{defaultContact?.phone} </td>
      <td className="py-0 pe-0 align-middle">{defaultContact?.mobile} </td>
      <td
        onDoubleClick={() => {
          copyContent(defaultContact?.email);
        }}
        className="py-2 pe-0 align-middle user-select-none"
      >
        {defaultContact?.email}
      </td>
      <td className="table-action py-0 text-end align-middle">
        {(user?.permissions?.includes("lead_update") ||
          user?.roles === "admin") && (
          <button
            onClick={() => {
              setFormData(defaultContact);
              setIsUpdateMode(true);
              setShowAddModal(true);
            }}
            className="btn btn-sm px-1"
          >
            <i className="mdi mdi-pencil"></i>
          </button>
        )}
        {(user?.permissions?.includes("lead_delete") ||
          user?.roles === "admin") && (
          <DeleteModalButton
            deleteFunc={deleteLeadContact}
            deleteParams={defaultContact?.contactId || defaultContact?.id}
            btnClass={"btn btn-sm px-1"}
            disableConditions={loading}
          />
        )}
      </td>
    </tr>
  );

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (selectedLead) {
        getLeadContacts();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedLead, getLeadContacts]);

  useEffect(() => {
    if (!showAddModal) {
      setFormData({});
      setIsUpdateMode(false);
    }
  }, [showAddModal]);

  return (
    <>
      {loading && <Spinner />}
      <div className="d-flex justify-content-between mb-1 align-items-center">
        <div className="d-flex align-items-center gap-5">{btnLeft()}</div>

        <div className="d-flex align-items-center gap-0">
          <div className="">
            Show &nbsp;
            <div className="btn-group">
              <button
                className="px-2 btn btn-outline-primary bg-primary bg-opacity-10 text-black"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {pageLimit} <i className="mdi mdi-chevron-down"></i>
              </button>
              <ul className="dropdown-menu w-25">
                <li onClick={() => setPageLimit(5)}>
                  <button className="dropdown-item">5</button>
                </li>
                <li onClick={() => setPageLimit(10)}>
                  <button className="dropdown-item">10</button>
                </li>
                <li onClick={() => setPageLimit(15)}>
                  <button className="dropdown-item">15</button>
                </li>
                <li onClick={() => setPageLimit(20)}>
                  <button className="dropdown-item">20</button>
                </li>
              </ul>
            </div>
          </div>
          {buttons()}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getLeadContacts({ query: searchTerm, page: 1 });
              } else {
                getLeadContacts({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getLeadContacts({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          <div className="">
            {(user?.permissions?.includes("lead_add") ||
              user?.roles === "admin") && (
              <button
                onClick={() => {
                  setShowAddModal(true);
                  setFormData({});
                  setIsUpdateMode(false);
                }}
                type="button"
                className="btn btn-primary  ms-1"
              >
                Add
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="" style={{ minHeight: "10vh", borderRadius: 15 }}>
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={leadContactData}
            disableSorting={true}
            actionsOnEnd={true}
            topRow={defaultContact ? topRow() : null}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      <Modal
        size="lg"
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
      >
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <h4>{isUpdateMode ? "Update Contact" : "Add Contact"}</h4>
            {(user?.permissions?.includes("customer_update") ||
              user?.roles === "admin") &&
              isUpdateMode && (
                <>
                  {!formData?.isDefault && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleDefaultSelect(formData);
                      }}
                      className="btn btn-sm btn-info me-1"
                      type="button"
                    >
                      Set Default
                    </button>
                  )}
                </>
              )}
          </div>
          <hr className="mt-0" />
          <form
            onSubmit={
              isUpdateMode ? handleUpdateLeadContact : handleAddLeadContact
            }
            className=""
          >
            <div className="row">
              <div className="col-6">
                <div className="mb-3">
                  <label className="form-label">First Name</label>
                  <input
                    value={formData?.firstName || ""}
                    onChange={(e) => {
                      setFormData({ ...formData, firstName: e.target.value });
                    }}
                    placeholder="Enter first name"
                    type="text"
                    className="form-control"
                  />
                </div>
                <div className="mb-3 me-1">
                  <label className="form-label">Last Name</label>
                  <input
                    value={formData?.lastName || ""}
                    onChange={(e) => {
                      setFormData({ ...formData, lastName: e.target.value });
                    }}
                    placeholder="Enter last name"
                    type="text"
                    className="form-control"
                  />
                </div>
                <div className="mb-3 me-1">
                  <label className="form-label">Department</label>
                  <input
                    value={formData?.department || ""}
                    onChange={(e) => {
                      setFormData({ ...formData, department: e.target.value });
                    }}
                    placeholder="Enter department"
                    type="text"
                    className="form-control"
                  />
                </div>
              </div>
              <div className="col-6 d-flex flex-column">
                <div className="mb-3">
                  <label className="form-label">Phone</label>
                  <input
                    value={formData?.phone || ""}
                    onChange={(e) => {
                      setFormData({ ...formData, phone: e.target.value });
                    }}
                    placeholder="Enter Phone"
                    className="form-control"
                  />
                </div>
                <div className="row mb-3">
                  <div className="col-7">
                    <label className="form-label"> Mobile Phone</label>
                    <input
                      value={formData?.mobile || ""}
                      onChange={(e) => {
                        setFormData({ ...formData, mobile: e.target.value });
                      }}
                      placeholder="Enter Phone"
                      className="form-control"
                    />
                  </div>
                  <div className="col-5 d-flex flex-column justify-content-around align-items-between">
                    <label className="">Sms Notification</label>
                    <input
                      type="checkbox"
                      id="leadSmsSwitch"
                      defaultChecked={formData?.smsNotification || false}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          smsNotification: e.target.checked,
                        });
                      }}
                      data-switch="none"
                    />
                    <label
                      htmlFor="leadSmsSwitch"
                      data-on-label="Yes"
                      data-off-label="No"
                    ></label>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    value={formData?.email || ""}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                    }}
                    placeholder="Enter email"
                    type="text"
                    className="form-control"
                  />
                </div>
              </div>
            </div>

            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default LeadContact;
